/**
 * Ported VERBATIM from `apr_template/src/pages/DataExportReports.tsx`.
 * Only import paths change. See ../compat/ for the shims.
 */
import React, { useState, useEffect } from 'react';
import { authFetch } from '../compat/api';
import './DataExportReports.css';
import './modelStatsBadge.css';
import '../components/RowControls.css';
import { useDispositionPalette } from '../dispositionPalette';
import PageHeader from '../compat/PageHeader';
import HelpPanel from '../compat/HelpPanel';
import { type Help } from '../compat/helpTypes';

const DataExportReportsHelp: Help = {
  title: 'Data Export & Reports',
  icon: '📤',
  purpose:
    'Export application portfolio data, scores, and Q&amp;A assessments to <strong>Excel</strong>. Choose which scored applications to include, then pick a report type to download.',
  doHere: [
    'Pick applications in the <strong>Select Applications</strong> tree (grouped by APR model); use <strong>Select All</strong> / <strong>Unselect All</strong> and per-model checkboxes.',
    '<strong>Expand All</strong> / <strong>Collapse All</strong> or toggle individual models to browse scored apps.',
    'Choose an export card and click its button: <strong>Executive Summary</strong>, <strong>APR Model Reports</strong>, <strong>Application Details</strong>, or <strong>Assessment Deep Dive</strong>.',
    'Only applications with a <strong>completed</strong> assessment and a score above 0 are available to export.',
    'Confirm the dialog to generate and download the <strong>.xlsx</strong> file.',
  ],
  fits:
    'Application Evaluation &rarr; Initial Disposition &rarr; Data Export &amp; Reports',
  firstTime:
    'Select a few scored applications, then click Export Summary to download your first report.',
  sections: [
    {
      type: 'defs',
      label: 'Export types',
      items: [
        { term: 'Executive Summary', color: '#0d47a1', def: 'Portfolio health, disposition distribution, model status.' },
        { term: 'APR Model Reports', color: '#4a148c', def: 'Model-level data with application summaries and dimension averages.' },
        { term: 'Application Details', color: '#155e75', def: 'Full app master data, dimension/component scores, and dispositions.' },
        { term: 'Assessment Deep Dive', color: '#9a3412', def: 'Individual Q&amp;A responses and scoring calculations.' },
      ],
    },
  ],
};

interface Application {
  id: string;
  name: string;
  applicationOwner: string;
  technologyStack: string;
  businessDomain: string;
  businessCriticality: string;
  userCount: string | number;
  /** `vendor_supplier` in master data. Drives the Vendor filter and column. */
  vendor: string;
  overallScore: number;
  recommendation: 'Retire' | 'Re-Platform' | 'Re-Design' | 'Retain' | 'Pending';
  completionPercentage: number;
  completedDate: string | null;
  totalQuestions: number;
  answeredQuestions: number;
  assessmentStatus: 'Completed' | 'In-Process' | 'Not Started';
}

interface APRModelGroup {
  modelId: string;
  modelName: string;
  modelStatus: string;
  modelCreatedAt: string;
  applications: Application[];
  selectedApps: Set<string>;
}

interface ExportFilter {
  applicationIds: string[];
  modelIds: string[];
  recommendations: string[];
  dateRange: {
    from: string;
    to: string;
  };
  includeDetails: boolean;
}

interface ScoringThreshold {
  id: string;
  decision_name: string;
  min_score: number;
  max_score: number;
  color: string;
  description: string;
  display_order: number;
  created_at: string;
  updated_at: string;
}

interface ApplicationCode {
  id: string;
  application_id_code: string;
  name: string;
}

/**
 * The four reports, as data rather than four near-identical blocks of JSX.
 *
 * They were written out four times, each with its own card markup, its own button and its
 * own emoji. One list means adding a fifth report is a line here, and means the four
 * cannot drift apart in wording or behaviour — which they already had: three said
 * "Export <noun>" and the labels did not match their own headings.
 *
 * Copy is verbatim from the cards it replaces.
 */
const EXPORT_REPORTS = [
  {
    key: 'executive' as const,
    name: 'Executive Summary',
    button: 'Export Summary',
    description: 'High-level dashboard metrics, disposition distribution, and model status overview',
    points: ['Portfolio health metrics', 'Recommendation distribution', 'Model completion status'],
  },
  {
    key: 'models' as const,
    name: 'APR Model Reports',
    button: 'Export Models',
    description: 'Detailed model-level data with application summaries and scores',
    points: ['Model details and timeline', 'Application summaries', 'Dimension score averages'],
  },
  {
    key: 'applications' as const,
    name: 'Application Details',
    button: 'Export Applications',
    description: 'Complete application data with scores, recommendations, and metadata',
    points: ['Application master data', 'Dimension & component scores', 'Recommendations'],
  },
  {
    key: 'assessments' as const,
    name: 'Assessment Deep Dive',
    button: 'Export Assessments',
    description: 'Full Q&A responses, scoring breakdown, and assessment analytics',
    points: ['Individual question responses', 'Detailed scoring calculations', 'Assessment timeline data'],
  },
];

const DataExportReports: React.FC = () => {
  // The one shared source for disposition colour — same store the other evaluation
  // screens read, so a change in Settings -> Disposition Matrix moves all of them.
  const palette = useDispositionPalette();
  const [modelGroups, setModelGroups] = useState<APRModelGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState<string | null>(null);
  const [filters] = useState<ExportFilter>({
    applicationIds: [],
    modelIds: [],
    recommendations: [],
    dateRange: { from: '', to: '' },
    includeDetails: false
  });
  const [totalSelected, setTotalSelected] = useState(0);
  const [scoringThresholds, setScoringThresholds] = useState<ScoringThreshold[]>([]);
  const [applicationCodes, setApplicationCodes] = useState<Map<string, string>>(new Map());
  const [collapsedModels, setCollapsedModels] = useState<Set<string>>(new Set());
  /** Which report the left panel has selected. One is always chosen; there is no null state. */
  const [selectedReport, setSelectedReport] = useState<typeof EXPORT_REPORTS[number]['key']>('executive');
  /** The two filters that replaced the Select Applications header. */
  const [appFilter, setAppFilter] = useState('');
  const [vendorFilter, setVendorFilter] = useState('');

  useEffect(() => {
    loadAPRModelsWithApplications();
    loadScoringThresholds();
    loadApplicationCodes();
  }, []);

  const loadApplicationCodes = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await authFetch('/api/applications', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        // The handler answers `{ applications: [...] }` — a bare payload keyed by the
        // resource, per docs/CONVENTIONS.md — not a bare array. Read as an array this
        // threw on .forEach, the catch swallowed it, and the map stayed empty. Nobody
        // noticed because the column fell back to the application NAME, in a column
        // headed "Application ID".
        const payload = await response.json();
        const applications: ApplicationCode[] = Array.isArray(payload)
          ? payload
          : payload?.applications ?? [];
        const codeMap = new Map<string, string>();
        applications.forEach(app => {
          if (app.application_id_code) {
            codeMap.set(app.id, app.application_id_code);
          }
        });
        console.log('🏷️ Loaded application codes:', codeMap);
        setApplicationCodes(codeMap);
      }
    } catch (error) {
      console.error('Failed to load application codes:', error);
    }
  };

  const loadScoringThresholds = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await authFetch('/api/scoring-thresholds', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const thresholds = await response.json();
        console.log('🎨 Loaded scoring thresholds:', thresholds);
        setScoringThresholds(thresholds);
      }
    } catch (error) {
      console.error('Failed to load scoring thresholds:', error);
    }
  };

  const loadAPRModelsWithApplications = async () => {
    setLoading(true);
    try {
      console.log('🚀 STARTING: Loading APR Models with applications...');
      const token = localStorage.getItem('authToken');
      const response = await authFetch('/api/export/models-with-applications', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      console.log('📡 Response status:', response.status, response.statusText);
      console.log('📡 Response OK:', response.ok);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Response error text:', errorText);
        throw new Error(`Failed to load APR models: ${response.statusText}`);
      }

      const modelData: APRModelGroup[] = await response.json();
      console.log('✅ RAW API response:', modelData);
      console.log('✅ API response length:', modelData.length);
      console.log('✅ API response type:', typeof modelData, Array.isArray(modelData));
      console.log('✅ First model data:', modelData[0]);

      // Add selectedApps set to each model group
      const modelGroups: APRModelGroup[] = modelData.map(model => ({
        ...model,
        selectedApps: new Set<string>()
      }));

      console.log('✅ Model groups created:', modelGroups);
      console.log('✅ Model groups length:', modelGroups.length);
      console.log('✅ First model group:', modelGroups[0]);
      console.log('✅ First model group applications:', modelGroups[0]?.applications);
      console.log('✅ ABOUT TO SET MODEL GROUPS');
      setModelGroups(modelGroups);

      // Initially collapse all models that have scored applications
      const modelIdsToCollapse = modelGroups
        .filter(group => group.applications.some(app => app.assessmentStatus === 'Completed' && app.overallScore > 0))
        .map(group => group.modelId);
      setCollapsedModels(new Set(modelIdsToCollapse));
      console.log('✅ MODEL GROUPS SET!');

      const totalApps = modelGroups.reduce((sum, model) => sum + model.applications.length, 0);
      console.log(`✅ SUCCESS: Loaded ${modelGroups.length} APR models with ${totalApps} total applications`);
    } catch (error) {
      console.error('❌ ERROR loading APR models with applications:', error);
      setModelGroups([]);
    } finally {
      console.log('🏁 FINISHED: Setting loading to false');
      setLoading(false);
    }
  };

  const handleApplicationSelection = (modelId: string, appId: string, selected: boolean) => {
    console.log('🔍 Application selection:', { modelId, appId, selected });
    setModelGroups(prev => prev.map(group => {
      if (group.modelId === modelId) {
        const newSelectedApps = new Set(group.selectedApps);
        if (selected) {
          newSelectedApps.add(appId);
        } else {
          newSelectedApps.delete(appId);
        }
        return { ...group, selectedApps: newSelectedApps };
      }
      return group;
    }));
  };

  const handleSelectAll = () => {
    setModelGroups(prev => prev.map(group => ({
      ...group,
      selectedApps: new Set(group.applications
        .filter(app => app.assessmentStatus === 'Completed' && app.overallScore > 0)
        .map(app => app.id))
    })));
  };

  const handleUnselectAll = () => {
    setModelGroups(prev => prev.map(group => ({
      ...group,
      selectedApps: new Set()
    })));
  };

  const handleModelSelection = (modelId: string, selected: boolean) => {
    setModelGroups(prev => prev.map(group => {
      if (group.modelId === modelId) {
        const scoredApps = group.applications.filter(app => app.assessmentStatus === 'Completed' && app.overallScore > 0);
        return {
          ...group,
          selectedApps: selected ? new Set(scoredApps.map(app => app.id)) : new Set()
        };
      }
      return group;
    }));
  };

  const isModelFullySelected = (group: APRModelGroup): boolean => {
    const scoredApps = group.applications.filter(app => app.assessmentStatus === 'Completed' && app.overallScore > 0);
    return scoredApps.length > 0 && scoredApps.every(app => group.selectedApps.has(app.id));
  };

  const isModelPartiallySelected = (group: APRModelGroup): boolean => {
    const scoredApps = group.applications.filter(app => app.assessmentStatus === 'Completed' && app.overallScore > 0);
    const selectedCount = scoredApps.filter(app => group.selectedApps.has(app.id)).length;
    return selectedCount > 0 && selectedCount < scoredApps.length;
  };

  // Update total selected count whenever modelGroups changes
  useEffect(() => {
    const total = modelGroups.reduce((sum, group) => sum + group.selectedApps.size, 0);
    setTotalSelected(total);
  }, [modelGroups]);

  // Toggle collapse/expand for a single model
  const toggleModelCollapse = (modelId: string) => {
    setCollapsedModels(prev => {
      const newSet = new Set(prev);
      if (newSet.has(modelId)) {
        newSet.delete(modelId);
      } else {
        newSet.add(modelId);
      }
      return newSet;
    });
  };

  // Expand all models
  const handleExpandAll = () => {
    setCollapsedModels(new Set());
  };

  // Collapse all models
  const handleCollapseAll = () => {
    const allModelIds = modelGroups
      .filter(group => group.applications.some(app => app.assessmentStatus === 'Completed' && app.overallScore > 0))
      .map(group => group.modelId);
    setCollapsedModels(new Set(allModelIds));
  };

  const handleExport = async (exportType: 'executive' | 'models' | 'applications' | 'assessments') => {
    const selectedAppIds = getAllSelectedApplicationIds();
    console.log('🔍 Export requested:', { exportType, selectedAppIds, totalSelected });
    console.log('🔍 Model groups state:', modelGroups.map(g => ({
      modelId: g.modelId,
      modelName: g.modelName,
      selectedApps: Array.from(g.selectedApps),
      selectedCount: g.selectedApps.size
    })));

    if (selectedAppIds.length === 0) {
      alert('Please select at least one application to export');
      return;
    }

    // Show confirmation dialog
    const exportTypeNames = {
      'executive': 'Executive Summary',
      'models': 'APR Model Reports', 
      'applications': 'Application Details',
      'assessments': 'Assessment Deep Dive'
    };

    const selectedModels = modelGroups.filter(group => group.selectedApps.size > 0).length;
    const confirmMessage = `🔍 Export Confirmation\n\n` +
      `Export Type: ${exportTypeNames[exportType]}\n` +
      `Selected Applications: ${selectedAppIds.length}\n` +
      `From APR Models: ${selectedModels}\n\n` +
      `This will generate an Excel file with your selected data.\n\n` +
      `Do you want to proceed with the download?`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    setExporting(exportType);
    try {
      const exportData = {
        applicationIds: selectedAppIds,
        exportType,
        filters: {
          ...filters,
          applicationIds: selectedAppIds
        },
        includeDetails: filters.includeDetails
      };

      console.log('Exporting data for', selectedAppIds.length, 'applications');
      
      // Use direct fetch call since apiService doesn't have export endpoints
      const token = localStorage.getItem('authToken');
      const response = await authFetch('/api/export/excel', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(exportData)
      });
      
      if (!response.ok) {
        throw new Error(`Export failed: ${response.statusText}`);
      }

      // Handle blob response
      const blob = await response.blob();

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `APR_${exportType}_export_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      console.log(`✅ Export completed - ${exportType} with ${selectedAppIds.length} applications`);

    } catch (error) {
      console.error('Export error:', error);
      alert('Export failed. Please try again.');
    } finally {
      setExporting(null);
    }
  };

  const getAllSelectedApplicationIds = (): string[] => {
    return modelGroups.reduce((ids: string[], group) => {
      return ids.concat(Array.from(group.selectedApps));
    }, []);
  };



  console.log('🎬 RENDER CYCLE - Loading:', loading, 'ModelGroups:', modelGroups, 'Length:', modelGroups.length);

  if (loading) {
    console.log('⏳ SHOWING LOADING SCREEN');
    return (
      <div className="export-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <h3>Loading APR Models...</h3>
          <p>Please wait while we fetch your data export options.</p>
        </div>
      </div>
    );
  }

  console.log('✅ NOT LOADING - About to render main content');
  console.log('🔍 About to render, modelGroups:', modelGroups);
  console.log('🔍 Model groups length in render:', modelGroups.length);

  if (modelGroups.length === 0) {
    console.log('⚠️ NO MODEL GROUPS - Showing empty state');
  } else {
    console.log('✅ HAVE MODEL GROUPS - Should show data table');
  }

  // Helper functions for styling
  const getRecommendationColor = (recommendation: string | null | undefined): string => {
    if (!recommendation || recommendation === 'Pending') {
      return '#9e9e9e';
    }
    
    console.log('🔍 Looking for color for recommendation:', recommendation);
    console.log('🔍 Available thresholds:', scoringThresholds);
    
    // Find the threshold that matches this recommendation name
    const threshold = scoringThresholds.find(t => 
      t.decision_name && t.decision_name.toLowerCase() === recommendation.toLowerCase()
    );
    
    console.log('🎨 Found threshold:', threshold);
    return threshold?.color || '#9e9e9e';
  };

  const getRecommendationIcon = (recommendation: string): string => {
    const icons: Record<string, string> = {
      'Retire': '🗑️',
      'Re-Platform': '🔧',
      'Re-Design': '🎨', 
      'Retain': '✅',
      'Pending': '⏳'
    };
    return icons[recommendation] || '❓';
  };

  const formatScore = (score: number | null | undefined): string => {
    if (score === null || score === undefined) return 'N/A';
    return score.toFixed(2);
  };

  /** A scored application is the only thing that can be exported. */
  const isScored = (app: Application) =>
    app.assessmentStatus === 'Completed' && app.overallScore > 0;

  /**
   * The two filters, applied together.
   *
   * The application box matches the ID or the name, because people search by whichever
   * they happen to have in front of them. Vendor is an exact match off the dropdown.
   */
  const matchesFilters = (app: Application) => {
    const needle = appFilter.trim().toLowerCase();
    if (needle) {
      const code = (applicationCodes.get(app.id) || '').toLowerCase();
      if (!app.name.toLowerCase().includes(needle) && !code.includes(needle)) return false;
    }
    if (vendorFilter && (app.vendor || '') !== vendorFilter) return false;
    return true;
  };

  const visibleApps = (group: APRModelGroup) => group.applications.filter(a => isScored(a) && matchesFilters(a));

  /** Every vendor present on a scored application, for the dropdown. */
  const vendorOptions = Array.from(
    new Set(
      modelGroups.flatMap(g => g.applications.filter(isScored).map(a => a.vendor).filter(Boolean)),
    ),
  ).sort((a, b) => a.localeCompare(b));

  const groupsWithMatches = modelGroups.filter(g => visibleApps(g).length > 0);
  const totalScored = modelGroups.reduce((sum, g) => sum + g.applications.filter(isScored).length, 0);
  const totalShown = modelGroups.reduce((sum, g) => sum + visibleApps(g).length, 0);
  const activeReport = EXPORT_REPORTS.find(r => r.key === selectedReport)!;

  return (
    <div className="page">
      <HelpPanel help={DataExportReportsHelp} />
      <PageHeader
        title="Data Export & Reports"
        subtitle="Export application portfolio data, assessments, and analytics to Excel for reporting and analysis"
      />

      <div className="master-data-container">
        {/* Two panels: the report on the left, what goes into it on the right.
            The four reports used to sit as cards across the top with the selection
            beneath, which put the choice and the thing being chosen a full screen
            apart. */}
        <div className="export-split">

          {/* ---------------- left: pick a report ---------------- */}
          <aside className="export-split__left">
            <h2 className="export-split__heading">Report</h2>

            {EXPORT_REPORTS.map(report => (
              <button
                key={report.key}
                type="button"
                className={`export-option${selectedReport === report.key ? ' export-option--on' : ''}`}
                onClick={() => setSelectedReport(report.key)}
                aria-pressed={selectedReport === report.key}
              >
                <span className="export-option__name">{report.name}</span>
                <span className="export-option__desc">{report.description}</span>
                <ul className="export-option__points">
                  {report.points.map(point => (
                    <li key={point}>{point}</li>
                  ))}
                </ul>
              </button>
            ))}

            {/* One button, for whichever report is selected — not four, one per card. */}
            <button
              className="export-btn"
              onClick={() => handleExport(activeReport.key)}
              disabled={totalSelected === 0 || exporting === activeReport.key}
            >
              {exporting === activeReport.key ? 'Exporting...' : activeReport.button}
            </button>
            {totalSelected === 0 && (
              <p className="export-split__hint">Select at least one application to export.</p>
            )}
          </aside>

          {/* ---------------- right: what goes into it ---------------- */}
          <section className="export-split__right">
            <h2 className="export-split__heading">Data selection</h2>

            <div className="export-filters">
              <div className="export-filters__field">
                <label htmlFor="export-filter-app">Applications</label>
                <input
                  id="export-filter-app"
                  type="text"
                  value={appFilter}
                  onChange={e => setAppFilter(e.target.value)}
                  placeholder="Name or ID..."
                />
              </div>
              <div className="export-filters__field">
                <label htmlFor="export-filter-vendor">Vendor</label>
                <select
                  id="export-filter-vendor"
                  value={vendorFilter}
                  onChange={e => setVendorFilter(e.target.value)}
                >
                  <option value="">All vendors</option>
                  {vendorOptions.map(vendor => (
                    <option key={vendor} value={vendor}>{vendor}</option>
                  ))}
                </select>
              </div>
              <div className="export-filters__count">
                {totalShown} of {totalScored} shown · <strong>{totalSelected} selected</strong>
              </div>
            </div>

            <div className="applications-table-container">
              {/* One header for the table, above the model groups and pinned while the
                  panel scrolls. It used to be re-rendered inside every group, which put
                  a fresh set of column names between each model and left you with none
                  at all once you had scrolled past the first. */}
              {totalScored > 0 && groupsWithMatches.length > 0 && (
                <div className="applications-table-header">
                  <div className="table-header-cell checkbox-col"></div>
                  <div className="table-header-cell">App ID</div>
                  <div className="table-header-cell">Application</div>
                  <div className="table-header-cell">Recommendation</div>
                  <div className="table-header-cell">Score</div>
                </div>
              )}
              {totalScored === 0 ? (
                /* `md-empty-state` — the same block Q&A, APR Model and Final Disposition
                   draw (source-css/app.css:2196). Text unchanged; it gains the icon and the
                   shared proportions so every APR empty state reads the same way. */
                <div className="md-empty-state">
                  <div className="md-empty-state__icon">📊</div>
                  <h3 className="md-empty-state__title">No scored applications yet</h3>
                  <p className="md-empty-state__description">
                    Once applications complete their Q&amp;A assessments and receive scores, they'll
                    appear here grouped by APR model — ready to select and export.
                  </p>
                </div>
              ) : groupsWithMatches.length === 0 ? (
                <div className="md-empty-state">
                  <div className="md-empty-state__icon">📊</div>
                  <h3 className="md-empty-state__title">Nothing matches those filters</h3>
                  <p className="md-empty-state__description">
                    Clear the application or vendor filter to see the rest of the portfolio.
                  </p>
                </div>
              ) : (
                groupsWithMatches.map(group => {
                  const shown = visibleApps(group);
                  const isFullySelected = isModelFullySelected(group);
                  const isPartiallySelected = isModelPartiallySelected(group);
                  const isCollapsed = collapsedModels.has(group.modelId);

                  return (
                    <div key={group.modelId} className="model-group">
                      <div className={`model-header-row ${isFullySelected ? 'selected' : ''}`}>
                        <button
                          className="collapse-toggle-btn"
                          onClick={e => {
                            e.stopPropagation();
                            toggleModelCollapse(group.modelId);
                          }}
                          title={isCollapsed ? 'Expand' : 'Collapse'}
                        >
                          {isCollapsed ? '▶' : '▼'}
                        </button>
                        <div className="model-header-checkbox">
                          <input
                            type="checkbox"
                            checked={isFullySelected}
                            ref={el => {
                              if (el) el.indeterminate = isPartiallySelected;
                            }}
                            onChange={e => handleModelSelection(group.modelId, e.target.checked)}
                            onClick={e => e.stopPropagation()}
                            className="table-checkbox"
                          />
                        </div>
                        <div className="model-header-info">
                          <span className="model-header-name">{group.modelName}</span>
                        </div>
                        <div className="model-header-stats">
                          <span className="model-stats-badge apps">
                            <span className="model-stats-badge__n">{shown.length}</span> apps
                          </span>
                          <span className="model-stats-badge selected">
                            <span className="model-stats-badge__n">
                              {shown.filter(app => group.selectedApps.has(app.id)).length}
                            </span> selected
                          </span>
                          <span className={`model-status-badge ${group.modelStatus.toLowerCase().replace(/\s+/g, '-')}`}>
                            {group.modelStatus}
                          </span>
                        </div>
                      </div>

                      {!isCollapsed && (
                        <div className="applications-table-body">
                            {shown.map(app => {
                              const isSelected = group.selectedApps.has(app.id);
                              const displayId = applicationCodes.get(app.id) || '—';

                              return (
                                <div
                                  key={app.id}
                                  className={`table-row ${isSelected ? 'selected' : ''}`}
                                  onClick={() => handleApplicationSelection(group.modelId, app.id, !isSelected)}
                                >
                                  <div className="table-cell checkbox-col">
                                    <input
                                      type="checkbox"
                                      checked={isSelected}
                                      onChange={e => handleApplicationSelection(group.modelId, app.id, e.target.checked)}
                                      onClick={e => e.stopPropagation()}
                                      className="table-checkbox"
                                    />
                                  </div>
                                  <div className="table-cell app-id-col">
                                    <span className="app-id-text">{displayId}</span>
                                  </div>
                                  <div className="table-cell app-name-col">
                                    <span className="app-name">{app.name}</span>
                                  </div>
                                  <div className="table-cell recommendation-col">
                                    {(() => {
                                      /* Filled Material 3 chip, matching Initial Portfolio
                                         Scoring / Q&A / Final Disposition. The fill is the
                                         matrix's own stored colour and the text is black or
                                         white by luminance — the previous treatment painted
                                         that colour as an outline over an 18%-alpha wash,
                                         which is why Re-Design still read as the old design
                                         after the other screens had moved. */
                                      const { base, onBase } = palette(app.recommendation);
                                      return (
                                        <span
                                          className="recommendation-badge-dynamic md-chip--disposition"
                                          style={{ backgroundColor: base, color: onBase }}
                                        >
                                          <span className="md-chip__dot" />
                                          {app.recommendation || 'Pending'}
                                        </span>
                                      );
                                    })()}
                                  </div>
                                  <div className="table-cell score-col">
                                    <div className="score-info-reports">
                                      <div className="score-value-reports">{formatScore(app.overallScore)}/5.0</div>
                                      <div className="score-bar-reports">
                                        <div
                                          className="score-bar-fill-reports"
                                          style={{
                                            width: `${(app.overallScore / 5) * 100}%`,
                                            backgroundColor: getRecommendationColor(app.recommendation),
                                          }}
                                        />
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default DataExportReports;